import { createSafeJotaiLocalStorage } from '@/lib/jotai-storage';
import type { ChatListItem } from '@/types';
import { atom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useEffect } from 'react';

/** Max persisted consult session ids (FIFO by append order; see atom comment). */
const DOC_EXPORT_BANNER_CLOSED_CONSULT_SESSION_IDS_MAX = 10;

const docExportBannerClosedConsultSessionIdsStorage = (() => {
  const base = createSafeJotaiLocalStorage<string[]>();
  const cap = (value: string[]): string[] =>
    value.length <= DOC_EXPORT_BANNER_CLOSED_CONSULT_SESSION_IDS_MAX
      ? value
      : value.slice(-DOC_EXPORT_BANNER_CLOSED_CONSULT_SESSION_IDS_MAX);

  return {
    ...base,
    getItem: (key: string, initialValue: string[]) => {
      const value = base.getItem(key, initialValue);
      if (!Array.isArray(value)) return initialValue;
      const asStrings = value.filter((id): id is string => typeof id === 'string');
      const capped = cap(asStrings);
      if (capped.length < asStrings.length) {
        base.setItem(key, capped);
      }
      return capped;
    },
    setItem: (key: string, value: string[]) => {
      base.setItem(key, cap(value));
    },
  };
})();

/**
 * Animation phases for the floating Ask Heidi interaction
 */
export type FloatingAskHeidiPhase =
  | 'idle' // Input visible at bottom, not focused
  | 'focused' // Input focused, ready for typing
  | 'typing' // User is typing
  | 'sending' // Input animating out after send
  | 'panel-opening' // Side panel sliding in
  | 'chat-animating' // Chat elements animating in sequentially
  | 'complete' // Animation complete, normal chat mode
  | 'returning'; // Input animating back in after panel closes

/**
 * Current animation phase for the floating Ask Heidi interaction
 */
export const floatingAskHeidiPhaseAtom = atom<FloatingAskHeidiPhase>('idle');

/**
 * Pending query text captured during the sending phase
 * This is stored so the chat panel can render it during animation
 */
export const floatingAskHeidiPendingQueryAtom = atom<string | null>(null);

/**
 * GRO-3269: text to prefill (not send) into the floating Ask Heidi input.
 * Set by the "Improve your notes" Ask Heidi spotlight so the example prompt
 * sits in the input ready to edit or send; the input consumes (clears) it
 * after inserting.
 */
export const floatingAskHeidiPrefillAtom = atom<string | null>(null);

/**
 * Whether the floating input should be visible based on animation phase
 * Note: Component also checks Redux sidebar state for robust visibility
 */
export const floatingAskHeidiInputVisibleAtom = atom((get) => {
  const phase = get(floatingAskHeidiPhaseAtom);
  return (
    phase === 'idle' ||
    phase === 'focused' ||
    phase === 'typing' ||
    phase === 'sending' ||
    phase === 'returning'
  );
});

/**
 * Whether the input is in "active" state (focused or typing)
 * Used to determine if the suggestions sheet should be shown
 */
export const floatingAskHeidiActiveAtom = atom((get) => {
  const phase = get(floatingAskHeidiPhaseAtom);
  return phase === 'focused' || phase === 'typing';
});

/**
 * Whether the suggestions sheet should be visible
 * Sheet shows when input is active (focused/typing) but not during sending/transition
 */
export const floatingAskHeidiSheetVisibleAtom = atom((get) => {
  const phase = get(floatingAskHeidiPhaseAtom);
  return phase === 'focused' || phase === 'typing';
});

/**
 * Whether the side panel should be open based on floating animation
 * When in panel-opening phase or beyond, panel should be visible
 */
export const floatingAskHeidiPanelOpenAtom = atom((get) => {
  const phase = get(floatingAskHeidiPhaseAtom);
  return phase === 'panel-opening' || phase === 'chat-animating' || phase === 'complete';
});

/**
 * Reset the floating Ask Heidi state to idle
 */
export const resetFloatingAskHeidiAtom = atom(null, (_get, set) => {
  set(floatingAskHeidiPhaseAtom, 'idle');
  set(floatingAskHeidiPendingQueryAtom, null);
});

/**
 * Whether the user has manually closed the sheet during this active session.
 * Used to prevent auto-reopening when user explicitly dismisses.
 */
export const userClosedSheetAtom = atom<boolean>(false);

/**
 * Ref-count of surfaces currently reporting a footer-occluding overlay.
 * Private: publish through `useFooterOverlayActiveSource` so concurrent
 * publishers compose instead of clobbering a peer with a plain boolean.
 */
const footerOverlayActiveSourceCountAtom = atom<number>(0);

/**
 * True while a peek/prompts overlay is rendered above the floating Ask Heidi
 * input. Published by `FloatingAskHeidiWithChips` and `EvidenceNoteOverlay`
 * (via `useFooterOverlayActiveSource`) and consumed by the note-tab footer so
 * it can collapse buttons that would otherwise be occluded by the overlay.
 * Read-only — derived from the source ref-count.
 */
export const floatingAskHeidiFooterOverlayActiveAtom = atom(
  (get) => get(footerOverlayActiveSourceCountAtom) > 0
);

/**
 * Publish, for the lifetime of the calling component, whether it currently
 * shows an overlay above the floating Ask Heidi input. Ref-counted so that
 * multiple concurrent publishers don't race: the overlay reads as active
 * while any source reports `active`. The count is decremented when `active`
 * goes false or the component unmounts.
 */
export const useFooterOverlayActiveSource = (active: boolean): void => {
  const setCount = useSetAtom(footerOverlayActiveSourceCountAtom);
  useEffect(() => {
    if (!active) return;
    setCount((count) => count + 1);
    return () => setCount((count) => Math.max(0, count - 1));
  }, [active, setCount]);
};

/**
 * Reset session-specific sheet state.
 * Called when session changes to reset user-closed state.
 */
export const resetSessionSheetStateAtom = atom(null, (_get, set) => {
  set(userClosedSheetAtom, false);
  set(floatingAskHeidiPhaseAtom, 'idle');
  set(floatingAskHeidiPendingQueryAtom, null);
  // selectedNoteTextAtom is NOT cleared here: this atom's only consumer fires on
  // every floating-bar remount (panel open/close), not just session change.
  // Session-scoped selection clear lives in FloatingAskHeidiWithChips.
  // The overlay-active count is intentionally NOT reset here: it is owned by
  // the mounted publishers (via `useFooterOverlayActiveSource`). Zeroing it
  // while a publisher is still reporting `active` would desync the count —
  // the publisher's effect wouldn't re-increment (its deps are unchanged), so
  // the overlay would read inactive while a sheet still occludes the footer.
});

/**
 * Consult session ids where the doc-export suggestion banner has already been
 * closed (dismissed or used a CTA). Persisted so the banner only shows once
 * per consult — not once per browser tab. The lifetime dismiss cap lives in
 * the backend `seen_modals` record (see `modalClosedRules.ts`).
 * Stored ids are capped (most recent wins) so localStorage cannot grow without bound.
 */
export const docExportBannerClosedConsultSessionIdsAtom = atomWithStorage<string[]>(
  'heidi:doc-export-suggestion-banner-closed-consults',
  [],
  docExportBannerClosedConsultSessionIdsStorage
);

/**
 * Selected chat for the floating input
 * null = new chat, ChatListItem = continue existing thread
 */
export const floatingAskHeidiSelectedChatAtom = atom<ChatListItem | null>(null);

/**
 * Derived atom for just the chatId of the selected chat
 */
export const floatingAskHeidiSelectedChatIdAtom = atom((get) => {
  const selectedChat = get(floatingAskHeidiSelectedChatAtom);
  return selectedChat?.id ?? null;
});
